import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useBrand } from '../context/BrandContext'
import { useNavigate } from 'react-router-dom'
import Chart from '../components/Chart'
import { colors, radius, s, ui, chartBase, lineGradient, areaGradient } from '../styles/theme'
import { useIsMobile } from '../hooks/useViewport'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000'

const style = {
  card: { ...s.cardSm, padding: '20px 24px' }
}

const fmt = n => parseFloat(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' ₺'

const kisaltDeger = n => {
  const v = Number(n) || 0
  if (v >= 1000000) return (v / 1000000).toLocaleString('tr-TR', { maximumFractionDigits: 1 }) + 'M'
  if (v >= 1000) return (v / 1000).toLocaleString('tr-TR', { maximumFractionDigits: 1 }) + 'B'
  return v.toLocaleString('tr-TR')
}

function InfluencerDashboard() {
  const { selectedBrand } = useBrand()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState('month')
  const token = localStorage.getItem('adminToken')
  const headers = { Authorization: token }

  useEffect(() => {
    if (!selectedBrand) return
    fetchDashboard()
  }, [selectedBrand?.id, period]) // eslint-disable-line

  function fetchDashboard() {
    setLoading(true)
    axios.get(`${API}/admin/brands/${selectedBrand.id}/influencer-dashboard?period=${period}`, { headers })
      .then(res => { setData(res.data); setLoading(false) })
      .catch(() => setLoading(false))
  }

  if (!selectedBrand) return (
    <div style={{ padding: '40px 0', maxWidth: '460px' }}>
      <p style={{ ...ui.notice, marginBottom: '16px' }}>Önce bir marka seçin.</p>
      <button onClick={() => navigate('/brands')} style={s.btnPrimary}>Markalara Git</button>
    </div>
  )

  const periods = [
    { value: 'today', label: 'Bugün' },
    { value: 'week', label: 'Bu Hafta' },
    { value: 'month', label: 'Bu Ay' }
  ]

  return (
    <div>
      <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
        <h2 style={{ ...s.pageTitle, minWidth: 0 }}>Influencer Dashboard — {selectedBrand.name}</h2>
        <div className="sx-wrap" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ ...ui.segmented, maxWidth: '100%' }}>
            {periods.map(p => (
              <button key={p.value} onClick={() => setPeriod(p.value)} style={ui.segmentedBtn(period === p.value)}>{p.label}</button>
            ))}
          </div>
          <button onClick={() => navigate('/influencers')} style={s.btnSecondary}> Influencer Yönetimi →
          </button>
        </div>
      </div>

      {loading ? <p style={ui.emptyText}>Yükleniyor...</p> : !data ? <p style={ui.emptyText}>Veri alınamadı.</p> : (
        <>
          {/* Özet Kartlar */}
          <div className="sx-grid-stat" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
            <SummaryCard label="Aktif Influencer" value={data.summary.active_influencers} />
            <SummaryCard label={`${periods.find(p => p.value === period)?.label} Satış`} value={fmt(data.summary.total_sales)} />
            <SummaryCard label={`${periods.find(p => p.value === period)?.label} Komisyon`} value={fmt(data.summary.total_commission)} />
            <SummaryCard label="Bekleyen Ödeme" value={fmt(data.summary.pending_payments)} />
          </div>

          {/* En İyi Performans */}
          <div className="sx-grid-panel" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            {/* En Çok Satan Influencer'lar */}
            <div style={style.card}>
              <h3 style={{ ...s.sectionTitle, margin: '0 0 16px' }}> En Çok Satan Influencer'lar</h3>
              {data.top_influencers.length === 0 ? (
                <p style={ui.emptyText}>Bu dönemde veri yok.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {data.top_influencers.map((inf, i) => (
                    <div key={inf.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', padding: '10px 12px', background: colors.bgPage, border: `1px solid ${colors.borderLight}`, borderRadius: radius.md }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: '1 1 140px' }}>
                        <span style={i === 0 ? ui.chipStrong : ui.chip}>{[1, 2, 3, 4, 5][i]}</span>
                        <div style={{ minWidth: 0 }}>
                          <p className="sx-break" style={{ margin: 0, fontWeight: 600, fontSize: '13px', color: colors.textPrimary }}>{inf.name}</p>
                          <p style={{ margin: 0, fontSize: '12px', color: colors.textSecondary }}>{inf.order_count} sipariş</p>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ ...ui.num, margin: 0 }}>{fmt(inf.total_sales)}</p>
                        <p style={{ ...ui.numMuted, margin: 0, fontSize: '12px' }}>{fmt(inf.total_commission)} komisyon</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* En Çok Satan Kampanyalar */}
            <div style={style.card}>
              <h3 style={{ ...s.sectionTitle, margin: '0 0 16px' }}> En Çok Satan Kampanyalar</h3>
              {data.top_campaigns.length === 0 ? (
                <p style={ui.emptyText}>Bu dönemde veri yok.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {data.top_campaigns.map((c, i) => (
                    <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', padding: '10px 12px', background: colors.bgPage, border: `1px solid ${colors.borderLight}`, borderRadius: radius.md }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: '1 1 140px' }}>
                        <span style={i === 0 ? ui.chipStrong : ui.chip}>{[1, 2, 3, 4, 5][i]}</span>
                        <div style={{ minWidth: 0 }}>
                          <p className="sx-break" style={{ margin: 0, fontWeight: 600, fontSize: '13px', color: colors.textPrimary }}>{c.name}</p>
                          <p className="sx-break" style={{ margin: 0, fontSize: '12px', color: colors.textSecondary }}>{c.influencer_name} · {c.order_count} sipariş</p>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ ...ui.num, margin: 0 }}>{fmt(c.total_sales)}</p>
                        <p style={{ ...ui.numMuted, margin: 0, fontSize: '12px' }}>{fmt(c.total_commission)} komisyon</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Trend Grafiği */}
          <div style={{ ...style.card, marginBottom: '24px' }}>
            <h3 style={{ ...s.sectionTitle, margin: '0 0 16px' }}> Son 30 Gün Satış Trendi</h3>
            {data.trend.length === 0 ? (
              <p style={ui.emptyText}>Henüz satış verisi yok.</p>
            ) : (
              <TrendChart data={data.trend} />
            )}
          </div>

          {/* Tüm Influencer'lar Tablosu */}
          <div style={style.card}>
            <h3 style={{ ...s.sectionTitle, margin: '0 0 16px' }}> Tüm Influencer'lar</h3>
            {data.influencer_table.length === 0 ? (
              <p style={ui.emptyText}>Henüz influencer yok.</p>
            ) : (
              <div className="sx-scroll-x">
              <table className="sx-cards" style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Influencer</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>Kampanya</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>Toplam Satış</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>Toplam Komisyon</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>Ödenen</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>Bakiye</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {data.influencer_table.map(inf => (
                    <tr key={inf.id}>
                      <td data-label="Influencer" style={s.td}>
                        <p style={{ margin: 0, fontWeight: 600 }}>{inf.name}</p>
                        <p style={{ margin: 0, fontSize: '12px', color: colors.textMuted }}>{inf.email}</p>
                      </td>
                      <td data-label="Kampanya" style={{ ...s.td, ...ui.numMuted, textAlign: 'center' }}>{inf.campaign_count}</td>
                      <td data-label="Toplam Satış" style={{ ...s.td, ...ui.num, textAlign: 'right' }}>{fmt(inf.total_sales)}</td>
                      <td data-label="Toplam Komisyon" style={{ ...s.td, ...ui.num, textAlign: 'right' }}>{fmt(inf.total_commission)}</td>
                      <td data-label="Ödenen" style={{ ...s.td, ...ui.numMuted, textAlign: 'right' }}>{fmt(inf.total_paid)}</td>
                      <td data-label="Bakiye" style={{ ...s.td, ...ui.num, textAlign: 'right' }}>{fmt(inf.balance)}</td>
                      <td data-label="Durum" style={{ ...s.td, textAlign: 'center' }}>
                        <span style={ui.status(inf.is_active)}><span style={ui.statusDot(inf.is_active)} />{inf.is_active ? 'Aktif' : 'Pasif'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

function SummaryCard({ label, value }) {
  return (
    <div style={ui.statBox}>
      <div className="sx-break" style={ui.statLabel}>{label}</div>
      <div className="sx-break" style={ui.statValue}>{value}</div>
    </div>
  )
}

function TrendChart({ data }) {
  const mobil = useIsMobile()
  const option = {
    color: chartBase.color,
    textStyle: chartBase.textStyle,
    grid: chartBase.grid,
    tooltip: {
      ...chartBase.tooltip,
      valueFormatter: v => fmt(v)
    },
    xAxis: {
      ...chartBase.xAxisStyle,
      type: 'category',
      boundaryGap: false,
      data: data.map(d => {
        const dt = new Date(d.date)
        return `${dt.getDate()}/${dt.getMonth() + 1}`
      })
    },
    yAxis: {
      ...chartBase.yAxisStyle,
      type: 'value',
      axisLabel: { ...chartBase.yAxisStyle.axisLabel, formatter: kisaltDeger }
    },
    series: [{
      name: 'Satış',
      type: 'line',
      smooth: true,
      showSymbol: false,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: { width: 2.5, color: lineGradient },
      itemStyle: { color: colors.primary },
      areaStyle: { color: areaGradient },
      data: data.map(d => Number(d.sales) || 0)
    }]
  }

  return <Chart option={option} height={mobil ? 210 : 260} />
}

export default InfluencerDashboard